/**
 * Modal sheets.
 *
 * One at a time, always dismissible, and always keyboard-complete: focus moves
 * in on open, cannot escape while open, and returns to whatever opened it on
 * close. Sheets are declared in the markup rather than built here, so a screen
 * reader sees the same document structure whether or not JavaScript has run.
 */
import { all, on } from '../util/dom';

/** How long a pointer press stays the best explanation for a sheet opening. */
const RECENT_PRESS_MS = 1000;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export class SheetHost {
  #scrim: HTMLElement;
  #open: HTMLElement | null = null;
  #restoreTo: HTMLElement | null = null;
  #detach: Array<() => void> = [];
  #onClose: (() => void) | null = null;
  #lastControl: HTMLElement | null = null;
  #lastControlAt = -Infinity;

  constructor(scrim: HTMLElement) {
    this.#scrim = scrim;
    on(scrim, 'click', () => this.close());

    // Safari does not focus a button when it is clicked, so `activeElement` at
    // open time is often the body, or a control inside the sheet being replaced.
    // Remembering the last control pressed is what lets focus come back to where
    // the person actually was.
    on(document, 'pointerdown', (e) => {
      const control = (e.target as HTMLElement | null)?.closest<HTMLElement>('button, a[href]');
      if (!control) return;
      this.#lastControl = control;
      this.#lastControlAt = performance.now();
    });
    on(document, 'keydown', (e) => {
      if (!this.#open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      } else if (e.key === 'Tab') {
        this.#trap(e);
      }
    });

    for (const sheet of all<HTMLElement>('.sheet')) {
      for (const closer of all<HTMLElement>('[data-close]', sheet)) {
        on(closer, 'click', () => this.close());
      }
    }
  }

  /**
   * The element focus should come back to, if it is still somewhere focus can
   * go. Closing one sheet to open another leaves `activeElement` pointing at a
   * control inside the sheet that just went away, and focusing that does
   * nothing at all — which reads to a keyboard user as focus being lost.
   */
  static #focusableNow(node: Element | null): HTMLElement | null {
    if (!(node instanceof HTMLElement)) return null;
    if (node === document.body || !node.isConnected) return null;
    return node.closest('[hidden]') ? null : node;
  }

  get current(): HTMLElement | null {
    return this.#open;
  }

  isOpen(sheet: HTMLElement): boolean {
    return this.#open === sheet;
  }

  open(sheet: HTMLElement, onClose?: () => void): void {
    if (this.#open === sheet) return;
    if (this.#open) this.close();

    // A press within the last moment is what opened this sheet, so it beats
    // whatever happens to hold focus. Keyboard activation fires no pointer
    // event, and there `activeElement` is already the right answer.
    const pressed = performance.now() - this.#lastControlAt < RECENT_PRESS_MS;
    this.#restoreTo =
      (pressed ? SheetHost.#focusableNow(this.#lastControl) : null) ??
      SheetHost.#focusableNow(document.activeElement) ??
      SheetHost.#focusableNow(this.#lastControl);
    this.#onClose = onClose ?? null;
    this.#open = sheet;

    this.#scrim.hidden = false;
    sheet.hidden = false;
    // Everything behind a modal sheet is inert to assistive technology too,
    // not just to the pointer.
    for (const sibling of all<HTMLElement>('body > *')) {
      if (sibling !== sheet && sibling !== this.#scrim) sibling.setAttribute('aria-hidden', 'true');
    }

    const first = sheet.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? sheet).focus({ preventScroll: true });
  }

  close(): void {
    const sheet = this.#open;
    if (!sheet) return;
    this.#open = null;

    sheet.hidden = true;
    this.#scrim.hidden = true;
    for (const sibling of all<HTMLElement>('body > *')) sibling.removeAttribute('aria-hidden');
    for (const off of this.#detach) off();
    this.#detach = [];

    this.#restoreTo?.focus({ preventScroll: true });
    this.#restoreTo = null;

    const done = this.#onClose;
    this.#onClose = null;
    done?.();
  }

  toggle(sheet: HTMLElement, onClose?: () => void): void {
    if (this.#open === sheet) this.close();
    else this.open(sheet, onClose);
  }

  #trap(e: KeyboardEvent): void {
    const sheet = this.#open;
    if (!sheet) return;
    const items = all<HTMLElement>(FOCUSABLE, sheet).filter((n) => n.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    if (e.shiftKey && (active === first || active === sheet)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
